import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';

function renderText(text: string) {
  return text.split('\n\n').map((block, blockIndex) => {
    const lines = block.split('\n');
    if (lines.length === 1) {
      const trimmed = lines[0].trim();
      if (trimmed.startsWith('•')) {
        return (
          <li key={blockIndex} className="text-sm text-foreground-600 font-body leading-relaxed ml-4 mb-1.5 list-disc marker:text-primary-500 break-words">
            {trimmed.replace(/^•\s*/, '')}
          </li>
        );
      }
      if (trimmed) {
        return (
          <p key={blockIndex} className="text-sm text-foreground-600 font-body leading-relaxed mb-3 last:mb-0 break-words">
            {trimmed}
          </p>
        );
      }
      return null;
    }

    const firstLineTrimmed = lines[0].trim();
    if (firstLineTrimmed.startsWith('•')) {
      return (
        <ul key={blockIndex} className="mb-3 last:mb-0 space-y-1.5">
          {lines.map((line, li) => {
            const t = line.trim();
            if (!t) return null;
            return (
              <li key={li} className="text-sm text-foreground-600 font-body leading-relaxed ml-4 list-disc marker:text-primary-500 break-words">
                {t.replace(/^•\s*/, '')}
              </li>
            );
          })}
        </ul>
      );
    }

    return (
      <p key={blockIndex} className="text-sm text-foreground-600 font-body leading-relaxed mb-3 last:mb-0 break-words">
        {lines.map((line, li) => (
          <span key={li}>
            {li > 0 && <br />}
            {line.trim()}
          </span>
        ))}
      </p>
    );
  });
}

export default function PrivacyPage() {
  const { t } = useTranslation();

  const sections = [
    { key: 's1' },
    { key: 's2' },
    { key: 's3' },
    { key: 's4' },
    { key: 's5' },
    { key: 's6' },
    { key: 's7' },
    { key: 's8' },
    { key: 's9' },
    { key: 's10' },
  ];

  return (
    <>
      <Head
        title={t('seo.privacy.title')}
        metas={[
          { name: 'description', content: t('seo.privacy.description') },
          { name: 'keywords', content: t('seo.privacy.keywords') },
          { property: 'og:title', content: t('seo.privacy.title') },
          { property: 'og:description', content: t('seo.privacy.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/privacy' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:title', content: t('seo.privacy.title') },
          { name: 'twitter:description', content: t('seo.privacy.description') },
          { name: 'last-modified', content: '2026-06-11' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/privacy' },
        ]}
        scripts={[
          {
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://estevaclinic.com/' },
                { '@type': 'ListItem', position: 2, name: t('privacy.title'), item: 'https://estevaclinic.com/privacy' },
              ],
            }),
          },
        ]}
      />
      <main className="bg-background-50 min-h-screen">
        <Navbar />

        <section className="relative pt-24 md:pt-32 pb-8 md:pb-12 px-6 md:px-10 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20architectural%20texture%20with%20soft%20warm%20beige%20and%20cream%20tones%2C%20elegant%20marble%20surface%20with%20subtle%20veins%2C%20luxurious%20spa%20atmosphere%2C%20soft%20diffused%20lighting%2C%20clean%20and%20sophisticated%20aesthetic%2C%20high-end%20interior%20design%20texture%2C%20no%20text%2C%20abstract%20background&width=1600&height=500&seq=privacy-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 to-foreground-950/90"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">
              {t('privacy.title')}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-300 font-body">
              {t('privacy.subtitle')}
            </p>
            <p className="mt-2 text-xs text-foreground-400 font-body italic">
              {t('privacy.date')}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-10 space-y-10 md:space-y-12">
              {sections.map(({ key }, idx) => (
                <div key={key} className={idx > 0 ? 'border-t border-background-200 pt-10 md:pt-12' : ''}>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                    {t(`privacy.${key}_title`)}
                  </h3>
                  <div>
                    {renderText(t(`privacy.${key}_text`))}
                  </div>
                </div>
              ))}

              <div className="border-t border-background-200 pt-10 md:pt-12 flex flex-wrap items-center gap-4">
                <Link
                  to="/imprint"
                  className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-5 py-3 min-h-[44px] rounded-full bg-primary-50 text-primary-700 text-xs tracking-[0.1em] uppercase font-label font-semibold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-file-list-3-line"></i>
                  {t('footer.imprint')}
                </Link>
                <Link
                  to="/agb"
                  className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-5 py-3 min-h-[44px] rounded-full bg-secondary-50 text-secondary-700 text-xs tracking-[0.1em] uppercase font-label font-semibold hover:bg-secondary-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-scales-line"></i>
                  {t('agb.title')}
                </Link>
              </div>

              <div className="border-t border-background-200 pt-8 md:pt-10">
                <p className="text-xs text-foreground-400 font-body italic text-center">
                  {t('privacy.last_updated')}: 11.06.2026
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}